'use client'

import React, { useCallback, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'

import type { HolidayItinerarySection } from '@/src/types/holidays'
import useClevertapForHolidays from '@/src/hooks/analytics/clevertap/holidays'
import { HOLIDAY_ACTION, HOLIDAY_ACTION_TYPE } from '@/src/utils/analytics/holidays/holidayEvents'

import ItineraryTimeline, { type ItineraryDayView } from './itineraryTimeline'

interface ItineraryTimelineWrapperProps {
    section: HolidayItinerarySection
    isMobile: boolean
}

/*
 * Jumped to, not animated to. A smooth scroll leaves a window in which the mobile sticky header
 * finishes its own transition and moves the target out from under the animation, which landed the
 * day a few hundred pixels off; an instant jump lands on it whatever else is still settling.
 */
const scrollToDay = (index: number) => {
    document.getElementById(`itinerary-day-${index}`)?.scrollIntoView({ block: 'start' })
}

const ItineraryTimelineWrapper: React.FC<ItineraryTimelineWrapperProps> = ({ section, isMobile }) => {
    const t = useTranslations('holidaysPage')
    const { triggerHolidayUIActionEvent } = useClevertapForHolidays()
    // Day 1 is open on load; the rest are collapsed so a six-day itinerary is still scannable.
    const [expandedIndex, setExpandedIndex] = useState(0)
    /** Day the rail asked for, scrolled to once the page has stopped moving. */
    const pendingScrollRef = useRef<number | null>(null)
    /** Whether that request also closes a day, whose collapse is what shifts the page. */
    const awaitsCollapseRef = useRef(false)

    // Only an opening day is an engagement; collapsing one is not.
    const trackDayOpened = useCallback(
        (index: number, engagementType: string) => {
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.CONTENT_ENGAGED, {
                action_type: HOLIDAY_ACTION_TYPE.CONTENT,
                component: 'itinerary',
                engagement_type: engagementType,
                day_index: index + 1
            })
        },
        [triggerHolidayUIActionEvent]
    )

    const handleToggleDay = useCallback(
        (index: number) => {
            const isExpanding = expandedIndex !== index
            setExpandedIndex(isExpanding ? index : -1)
            if (isExpanding) trackDayOpened(index, 'expand')
        },
        [expandedIndex, trackDayOpened]
    )

    const handleSelectDay = useCallback(
        (index: number) => {
            trackDayOpened(index, 'day_rail')

            // Already open, so nothing will move: scroll straight away.
            if (index === expandedIndex) {
                scrollToDay(index)
                return
            }
            pendingScrollRef.current = index
            awaitsCollapseRef.current = expandedIndex !== -1
            setExpandedIndex(index)
        },
        [expandedIndex, trackDayOpened]
    )

    /*
     * Scrolling on click aims at a stale position. MUI runs the close and the open at the same
     * time, and it is the closing day — which sits above the target — that moves the page. So the
     * scroll waits for that collapse; only when nothing was open does the open itself settle it.
     */
    const handleDayExpanded = useCallback((index: number) => {
        if (pendingScrollRef.current !== index || awaitsCollapseRef.current) return
        pendingScrollRef.current = null
        scrollToDay(index)
    }, [])

    const handleDayCollapsed = useCallback(() => {
        const pending = pendingScrollRef.current
        if (pending === null) return
        pendingScrollRef.current = null
        awaitsCollapseRef.current = false
        scrollToDay(pending)
    }, [])

    if (!section.days?.length) return null

    /*
     * The day title is written as pipe-separated stops — "Arrival in Helsinki | Transfer to Hotel |
     * Helsinki City Tour" — so it is split into the day's stops and shown as chips. A title without
     * pipes is one stop, and renders as the plain heading it already was.
     */
    const days: ItineraryDayView[] = section.days.map(day => {
        const stops = day.title
            .split('|')
            .map(part => part.trim())
            .filter(Boolean)

        return {
            dayLabel: day.dayLabel,
            title: day.title,
            stops: stops.length > 1 ? stops : [],
            highlights: day.highlights,
            images: day.images,
            thumbnail: day.images?.[0]
        }
    })

    return (
        <ItineraryTimeline
            days={days}
            expandedIndex={expandedIndex}
            onToggleDay={handleToggleDay}
            onSelectDay={handleSelectDay}
            onDayExpanded={handleDayExpanded}
            onDayCollapsed={handleDayCollapsed}
            railTitle={t('itineraryDayRail')}
            isMobile={isMobile}
        />
    )
}

export { ItineraryTimelineWrapper }
export default ItineraryTimelineWrapper
